import "./SelectDealKpisPanel.css";

import { DragEvent } from "react";

import {
  CalendarDarkIcon,
  HamburgerMenuGreyIcon,
  PercentIcon,
  PoundIcon,
  SortArrowsDarkIcon,
} from "../../../../../assets";
import { Button, FlyoutPanel } from "../../../../../widget";
import { DealKpiCategory, DealKpiKey, DealKpiOption } from "./models";
import { PanelMode, SORTED_DEAL_KPI_OPTIONS, useSelectDealKpisPanel } from "./useSelectDealKpisPanel";

interface SelectDealKpisPanelProps {
  show: boolean;
  selectedKpiKeys: DealKpiKey[];
  onClose: () => void;
  onSave: (selectedKpiKeys: DealKpiKey[]) => void;
}

const getCategoryIcon = (category: DealKpiCategory): JSX.Element => {
  switch (category) {
    case "Dates":
      return <CalendarDarkIcon className="SelectDealKpiCategoryIcon" />;
    case "Percent":
      return <PercentIcon className="SelectDealKpiCategoryIcon" />;
    case "Currency":
    case "Multiple":
    default:
      return <PoundIcon className="SelectDealKpiCategoryIcon" />;
  }
};

const renderSelectCard = (
  option: DealKpiOption,
  isSelected: boolean,
  atMax: boolean,
  toggleKpi: (key: DealKpiKey) => void
): JSX.Element => {
  const isDisabled = !isSelected && atMax;
  return (
    <button
      key={option.key}
      type="button"
      className={`SelectDealKpiCard${isSelected ? " Selected" : ""}${isDisabled ? " Disabled" : ""}`}
      disabled={isDisabled}
      onClick={() => toggleKpi(option.key)}
    >
      <span className="SelectDealKpiIconWrapper">{getCategoryIcon(option.category)}</span>
      <span className="SelectDealKpiInfo">
        <span className="SelectDealKpiLabel body1">{option.label}</span>
        <span className="SelectDealKpiDescription">{option.description}</span>
      </span>
      <span className={`SelectDealKpiCheckbox${isSelected ? " Checked" : ""}`} aria-hidden="true" />
    </button>
  );
};

const renderReorderCard = (
  option: DealKpiOption,
  index: number,
  onKpiDrop: (droppedOnKey: DealKpiKey, draggedKey: DealKpiKey) => void
): JSX.Element => (
  <button
    key={option.key}
    type="button"
    className="SelectDealKpiCard"
    draggable
    onDrop={(e: DragEvent<HTMLButtonElement>) => {
      e.preventDefault();
      onKpiDrop(option.key, e.dataTransfer.getData("dealKpiKey") as DealKpiKey);
    }}
    onDragOver={(e) => e.preventDefault()}
    onDragStart={(e) => e.dataTransfer.setData("dealKpiKey", option.key)}
  >
    <span className="SelectDealKpiPosition">{index + 1}</span>
    <span className="SelectDealKpiInfo">
      <span className="SelectDealKpiLabel body1">{option.label}</span>
      <span className="SelectDealKpiDescription">{option.description}</span>
    </span>
    <HamburgerMenuGreyIcon className="SelectDealKpiHandle" />
  </button>
);

const renderModeButton = (mode: PanelMode, setMode: (mode: PanelMode) => void): JSX.Element =>
  mode === "select" ? (
    <button type="button" className="SelectDealKpisModeBtn" onClick={() => setMode("reorder")}>
      Re-order
      <SortArrowsDarkIcon className="SelectDealKpisModeIcon" />
    </button>
  ) : (
    <button type="button" className="SelectDealKpisModeBtn" onClick={() => setMode("select")}>
      Save order
    </button>
  );

export const SelectDealKpisPanel = ({
  show,
  selectedKpiKeys,
  onClose,
  onSave,
}: SelectDealKpisPanelProps): JSX.Element => {
  const {
    mode,
    setMode,
    draftSelectedKpiKeys,
    selectedOptions,
    atMax,
    toggleKpi,
    onKpiDrop,
    handleSave,
    getStatusText,
  } = useSelectDealKpisPanel({ show, selectedKpiKeys, onClose, onSave });

  const content = (
    <div className="SelectDealKpisPanelContent">
      <div className="SelectDealKpisList">
        {mode === "select"
          ? SORTED_DEAL_KPI_OPTIONS.map((o) =>
              renderSelectCard(o, draftSelectedKpiKeys.includes(o.key), atMax, toggleKpi)
            )
          : selectedOptions.map((o, i) => renderReorderCard(o, i, onKpiDrop))}
      </div>
      <p className="SelectDealKpisHelpText body1">{getStatusText()}</p>
      <div className="SelectDealKpisActions">
        <Button text="Save" fullWidth corners="rounded" onClick={handleSave} />
        <Button text="Cancel" variant="tertiary" fullWidth corners="rounded" onClick={onClose} />
      </div>
    </div>
  );

  return (
    <FlyoutPanel
      show={show}
      onClose={onClose}
      size="ExtraSmall"
      header="Select KPIs"
      buttonsChildren={renderModeButton(mode, setMode)}
      contentChildren={content}
    />
  );
};
